// Die Ansichts-Bedienelemente der Projektseite — rechts auf der Tab-Zeile.
//
// WOFUER: die Projektseite hatte ZWEI Werkzeugzeilen uebereinander. Zeile 1
// (Ansicht ▾, Typ ▾, „Erledigte ausblenden", Ansichten ▾, Sichtung) gehoerte
// `routes/Project.tsx`, Zeile 2 (+ Neue Aufgabe, Suche, Filter-Chips,
// Gruppieren ▾, Spalten ▾) gehoert `views/TableView.tsx`.
//
// Zeile 1 ist damit AUFGELOEST: der Ansichts-Umschalter ist zu `ViewTabs` in
// der Kopfzeile geworden, und die vier hier verbliebenen Bedienelemente sitzen
// jetzt rechts NEBEN diesen Tabs — auf derselben Zeile, kein eigenes Band mehr.
// Im Inhalt bleibt damit genau EINE Werkzeugzeile uebrig, die von TableView.
//
// Warum nicht in `actions`: das sind Ansichts-, keine Projektaktionen. Sie
// gehoeren zu den Tabs, mit denen sie die Ansicht steuern — und `actions`
// traegt bereits fuenf Projektaktionen.
//
// Umbruch statt Quetschen: auf schmalen Fenstern rutscht die Gruppe unter die
// Tabs (`flex-wrap` auf der Zeile), die Tabs selbst scrollen waagerecht
// (ViewTabs bringt das mit).

import type { JSX } from "preact";
import { Inbox } from "lucide-preact";
import { SavedViews, type ViewSpec } from "../../components/SavedViews";
import { FilterControls } from "../../components/GroupSortFilterBar";
import { DEFAULT_VIEW_STATE, type FilterRule } from "../../lib/viewState";
import type { GroupBy } from "../../lib/grouping";
import type { TableColumnId } from "../../lib/columns";

export interface ProjectViewControlsProps {
  projectId: number;
  view: string;
  groupBy: GroupBy;
  hiddenColumns: Set<TableColumnId>;
  tableFilter: string;
  viewFilters: FilterRule[];
  onFiltersChange: (next: FilterRule[]) => void;
  onApplySavedView: (spec: ViewSpec) => void;
  triageOpen: boolean;
  triageCount: number;
  onToggleTriage: () => void;
}

export function ProjectViewControls(
  props: ProjectViewControlsProps,
): JSX.Element {
  const {
    projectId,
    view,
    groupBy,
    hiddenColumns,
    tableFilter,
    viewFilters,
    onFiltersChange,
    onApplySavedView,
    triageOpen,
    triageCount,
    onToggleTriage,
  } = props;

  return (
    <div
      class="flex flex-wrap items-center gap-2 pb-1"
      data-testid="project-view-controls"
    >
      {/* #872/#873: die geteilten Filter fuer ALLE Ansichten. Sie liegen hier
          und nicht in einer einzelnen Ansicht, weil immer nur eine Ansicht
          sichtbar ist — ein Bedienelement in TableView koennte Kanban,
          Kalender und Zeitleiste nie bedienen. Gleiche Komponente wie im
          persoenlichen Bereich. */}
      <FilterControls
        projectId={projectId}
        state={{ ...DEFAULT_VIEW_STATE, filters: viewFilters }}
        onChange={(next) => onFiltersChange(next.filters)}
      />

      <SavedViews
        projectId={projectId}
        currentViewType={view}
        groupBy={groupBy}
        hiddenColumns={hiddenColumns}
        filter={tableFilter}
        onApply={onApplySavedView}
      />

      <button
        type="button"
        onClick={onToggleTriage}
        class={`inline-flex min-h-[36px] items-center gap-1.5 rounded border px-2.5 py-1 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 sm:min-h-[28px] ${
          triageOpen
            ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary-container)] text-[color:var(--color-on-primary-container)]"
            : "border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)]"
        }`}
        aria-pressed={triageOpen}
        title="Eingereichte Aufgaben sichten"
        aria-label="Sichtungsbereich ein-/ausblenden"
        data-testid="triage-toggle"
      >
        <Inbox size={14} aria-hidden="true" />
        Sichtung
        {triageCount > 0 && (
          <span
            class="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[color:var(--color-primary)] px-1 text-label font-semibold leading-none text-[color:var(--color-on-primary)]"
            aria-label={`${triageCount} ausstehend`}
          >
            {triageCount}
          </span>
        )}
      </button>
    </div>
  );
}
